import React from "react";
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Divider,
  useColorModeValue,
  Icon,
  Flex,
  useBreakpointValue,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import BudgetPlannerTripsDropdown from "./BudgetPlannerTripsDropdown";
import SettingsMenu from "../common/SettingsMenu";
import { FiPieChart, FiCreditCard, FiTag } from "react-icons/fi"; // Added FiTag for categories
import Translate from "../../components/LanguageTranslation/Translate";
import TravelGroupCircles from "../common/TravelGroupCircles";
import BudgetHeader from "./BudgetHeader";

const BudgetMenu = ({
  selectedTrip,
  setSelectedTrip,
  tripName,
  setTripName,
  setView,
  view,
  onOpen,
  onOpenCategoryModal,
  expenses,
  totalBudget,
  onShareOpen,
}) => {
  const bg = useColorModeValue("white", "gray.800");
  const buttonBg = useColorModeValue("#FFC107", "#FFC107");
  const buttonText = useColorModeValue("black", "black");
  const isBase = useBreakpointValue({ base: true, md: false });
  const isLg = useBreakpointValue({
    base: false,
    md: true,
    lg: true,
    xl: true,
  });
  const buttons = [
    { icon: FiPieChart, label: "Stats", onClick: () => setView("stats") },
    {
      icon: FiCreditCard,
      label: "Expenses",
      onClick: () => setView("expenses"),
    },
    { icon: FiTag, label: "Categories", onClick: () => setView("categories") },
  ];
  return (
    <Box
      w={{ base: "100%", md: "20%", lg: "25%" }}
      display="flex"
      flexDirection="column"
      borderRight="1px solid"
      borderColor="blackAlpha.200"
      bg={bg}
      h={{ base: "auto", md: "100vh", lg: "100vh" }}
      overflowY={{ base: "none", md: "auto", lg: "auto" }}
      mb={{ base: 4, md: 0, lg: 0 }}
    >
      {/* Budget Heading and Settings */}
      <BudgetHeader
        tripName={tripName}
        expenses={expenses}
        totalBudget={totalBudget}
        onShareOpen={onShareOpen}
        selectedTrip={selectedTrip}
        isBase={isBase}
      />

      {/* Trip Dropdown */}
      <VStack p={4} pt={0} pb={0} align="left" justifyContent="flex-start">
        <BudgetPlannerTripsDropdown
          selectedTrip={selectedTrip}
          setSelectedTrip={setSelectedTrip}
          setTripName={setTripName}
          onTripSelect={(trip) => {
            setSelectedTrip(trip);
            setView("stats");
          }}
        />
        {isLg && selectedTrip &&  (
          <TravelGroupCircles
            tripId={selectedTrip}
            availableResource="budget_planner"
          />
        )}
      </VStack>

      {/* Options for Stats, Expenses, and Categories */}
      {isBase ? (
        // Horizontal stack for base screen
        <HStack
          spacing={2}
          p={2}
          pt={4}
          pb={4}
          width="100%"
          justifyContent="center"
        >
          <HStack
            // width={{ base: "50%", md: "100%", lg: "100%" }}
            bg={selectedTrip && view === "stats" ? "#286328" : "blackAlpha.50"}
            color={selectedTrip && view === "stats" ? "#ffffff" : "inherit"}
            p={2}
            pl={3}
            borderRadius="full"
            cursor={!selectedTrip ? "not-allowed" : "pointer"}
            opacity={!selectedTrip ? 0.5 : 1}
            onClick={!selectedTrip ? undefined : () => setView("stats")} // Disable click if no trip selected
          >
            <Icon as={FiPieChart} boxSize={5} />
            <Text fontSize="sm" fontWeight="medium" mb={0}>
              <Translate text="Overview" />
            </Text>
          </HStack>

          <HStack
            // width={{ base: "50%", md: "100%", lg: "100%" }}
            bg={
              selectedTrip && view === "expenses" ? "#286328" : "blackAlpha.50"
            }
            color={selectedTrip && view === "expenses" ? "#ffffff" : "inherit"}
            p={2}
            pl={3}
            borderRadius="full"
            cursor={!selectedTrip ? "not-allowed" : "pointer"}
            opacity={!selectedTrip ? 0.5 : 1}
            onClick={!selectedTrip ? undefined : () => setView("expenses")} // Disable click if no trip selected
          >
            <Icon as={FiCreditCard} boxSize={5} />
            <Text fontSize="sm" fontWeight="medium" mb={0}>
              <Translate text="Expenses" />
            </Text>
          </HStack>

          <HStack
            // width={{ base: "50%", md: "100%", lg: "100%" }}
            bg={
              selectedTrip && view === "categories"
                ? "#286328"
                : "blackAlpha.50"
            }
            color={
              selectedTrip && view === "categories" ? "#ffffff" : "inherit"
            }
            p={2}
            pl={3}
            borderRadius="full"
            cursor={!selectedTrip ? "not-allowed" : "pointer"}
            opacity={!selectedTrip ? 0.5 : 1}
            onClick={!selectedTrip ? undefined : () => setView("categories")} // Disable click if no trip selected
          >
            <Icon as={FiTag} boxSize={5} />
            <Text fontSize="sm" fontWeight="medium" mb={0}>
              <Translate text="Categories" />
            </Text>
          </HStack>
        </HStack>
      ) : (
        <VStack
          spacing={4}
          align="flex-start"
          p={4}
          pt={4}
          width={{ base: "100%", md: "100%", lg: "100%" }}
        >
          <Divider m={0} p={0} />

          <HStack
            width={{ base: "50%", md: "100%", lg: "100%" }}
            bg={selectedTrip && view === "stats" ? "#286328" : "blackAlpha.50"}
            color={selectedTrip && view === "stats" ? "#ffffff" : "inherit"}
            p={2}
            pl={3}
            borderRadius="full"
            cursor={!selectedTrip ? "not-allowed" : "pointer"}
            opacity={!selectedTrip ? 0.5 : 1}
            onClick={!selectedTrip ? undefined : () => setView("stats")} // Disable click if no trip selected
          >
            <Icon as={FiPieChart} boxSize={5} />
            <Text fontSize="md" fontWeight="medium" mb={0}>
              <Translate text="Overview" />
            </Text>
          </HStack>

          <HStack
            width={{ base: "50%", md: "100%", lg: "100%" }}
            bg={
              selectedTrip && view === "expenses" ? "#286328" : "blackAlpha.50"
            }
            color={selectedTrip && view === "expenses" ? "#ffffff" : "inherit"}
            p={2}
            pl={3}
            borderRadius="full"
            cursor={!selectedTrip ? "not-allowed" : "pointer"}
            opacity={!selectedTrip ? 0.5 : 1}
            onClick={!selectedTrip ? undefined : () => setView("expenses")} // Disable click if no trip selected
          >
            <Icon as={FiCreditCard} boxSize={5} />
            <Text fontSize="md" fontWeight="medium" mb={0}>
              <Translate text="Expenses" />
            </Text>
          </HStack>

          <HStack
            width={{ base: "50%", md: "100%", lg: "100%" }}
            bg={
              selectedTrip && view === "categories"
                ? "#286328"
                : "blackAlpha.50"
            }
            color={
              selectedTrip && view === "categories" ? "#ffffff" : "inherit"
            }
            p={2}
            pl={3}
            borderRadius="full"
            cursor={!selectedTrip ? "not-allowed" : "pointer"}
            opacity={!selectedTrip ? 0.5 : 1}
            onClick={!selectedTrip ? undefined : () => setView("categories")} // Disable click if no trip selected
          >
            <Icon as={FiTag} boxSize={5} />
            <Text fontSize="md" fontWeight="medium" mb={0}>
              <Translate text="Categories" />
            </Text>
          </HStack>

          <Divider m={0} p={0} />
        </VStack>
      )}

      {/* <Flex
        display="flex"
        align="left"
        direction={{ base: "row", md: "column" }}
      >
       
        <Box mb={0} p={{ base: 2, md: 4, lg: 4 }} pt={0}  display={{base: "none", md: "block"}}>
          <Button
            width={{ base: "100%", md: "100%", lg: "100%" }}
            bg="#286328"
            color="white"
            _hover="none"
            borderRadius="full"
            leftIcon={<AddIcon />}
            onClick={onOpen}
            isDisabled={!selectedTrip} // Disable button if no trip is selected
          >
            <Translate text="Add Expense" />
          </Button>
        </Box>

        
        <Box p={{ base: 2, md: 4, lg: 4 }} pb={0} pt={0} display={{base: "none", md: "block"}}>
          <Button
            width={{ base: "100%", md: "100%", lg: "100%" }}
            bg="#286328"
            color="white"
            _hover="none"
            borderRadius="full"
            leftIcon={<AddIcon />}
            onClick={onOpenCategoryModal}
            isDisabled={!selectedTrip} // Disable button if no trip is selected
          >
            <Translate text="Add Category" />
          </Button>
        </Box>
      </Flex> */}
    </Box>
  );
};

export default BudgetMenu;
